// PRD §3.3 — milk production schemas (DAIRY animals only).
import { z } from 'zod';
export const MilkProductionResponseSchema = z.object({
    id: z.string().uuid(),
    animal_id: z.string().uuid(),
    date: z.string(),
    liters: z.number().nonnegative(),
    fat_pct: z.number().nullable(),
    protein_pct: z.number().nullable(),
    somatic_cells: z.number().int().nullable(),
    lactose_pct: z.number().nullable(),
});
export const MilkPageResponseSchema = z.object({
    rows: z.array(MilkProductionResponseSchema),
    next_cursor: z.string().nullable(),
});
// PRD M10.3 finding #10 — querystring schema for the milk timeline page.
// Promoted out of `backend/src/routes/v1/milk.ts` so the contract audit
// can verify it (and so a future mobile screen consuming this endpoint can
// type its query params off the same source of truth).
export const MilkPageQuerySchema = z
    .object({
    cursor: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
})
    .strict();
// PRD v3 Phase 22 M1 — write contract for `POST /animals/:id/milk`.
//
// The body accepts either a single `liters` value (already-aggregated total
// for the day) OR two split readings (`liters_morning` + `liters_evening`)
// which the server sums into `liters`. If both arrive, the split sum wins —
// it carries the milking-time detail and the gandeiro flagged it explicitly.
//
// Idempotency: the (animal_id, date) tuple is unique server-side; resubmitting
// the same date overwrites the previous row. The `liters_morning`/`evening`
// fields are validation-only — only the aggregate `liters` is persisted.
const NonNegFloat = z.number().nonnegative();
export const MilkProductionInputSchema = z
    .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be ISO YYYY-MM-DD'),
    liters: NonNegFloat.optional(),
    liters_morning: NonNegFloat.optional(),
    liters_evening: NonNegFloat.optional(),
    fat_pct: NonNegFloat.optional(),
    protein_pct: NonNegFloat.optional(),
    somatic_cells: z.number().int().nonnegative().optional(),
    lactose_pct: NonNegFloat.optional(),
    milking_time: z.string().datetime({ offset: true }).optional(),
})
    .strict()
    .refine((v) => v.liters !== undefined || v.liters_morning !== undefined || v.liters_evening !== undefined, {
    message: 'liters or liters_morning/liters_evening is required',
    path: ['liters'],
});
/**
 * Resolve the effective `liters` total from a validated input. Split readings
 * win over the single aggregate when both arrive (PRD §M1 — "if both arrive,
 * the server prefers morning+evening"). When only `liters_*` are present we
 * sum them; when only `liters` is present we use it as-is.
 */
export function resolveMilkLiters(input) {
    const hasMorning = input.liters_morning !== undefined;
    const hasEvening = input.liters_evening !== undefined;
    if (hasMorning || hasEvening) {
        return (input.liters_morning ?? 0) + (input.liters_evening ?? 0);
    }
    return input.liters ?? 0;
}
//# sourceMappingURL=milk.js.map